import React from 'react'
import { Stack, Typography } from '@mui/material'
import { Button } from '@repo/ui'
import { ChevronDownIcon } from 'lucide-react'

/**
 * The `Label: Value ˅` trigger for a compact in-card range/mode picker.
 *
 * Extracted at the SECOND use (working-conventions §36): the activation-trigger
 * control had it, the chart-range control needed exactly the same thing, and the
 * two now sit side by side in the card's top band where any divergence in weight,
 * spacing or chevron size would be read immediately. One source means they cannot
 * drift into looking like two different kinds of control.
 *
 * PROMOTED out of `components/display/BillingOverview/` at the THIRD use — the
 * analytics ROI hero's own period picker (`RoiHero`), which is the same control
 * answering the same question ("which window is this card measured over?") on a
 * different surface. A shared component living inside one feature folder is a
 * shared component the next feature will not find, so it moved rather than being
 * imported across features (§36/§47).
 *
 * Only the TRIGGER is shared, not the menu. The two menus are genuinely different
 * shapes — the trigger picker's rows are three lines each with a description and a
 * "Best for" line, the range picker's are a single label — and flattening both
 * into one generic `options[]` API would cost more in indirection than the
 * duplication it removes.
 */
export const InlineSelectButton = ({
  label,
  value,
  onClick,
  ariaLabel,
  emphasis = 'control',
}: {
  /** The quiet half — what is being chosen ("Activated:", "Chart:"). */
  label: string
  /** The loud half — the current choice. */
  value: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  ariaLabel?: string
  /**
   * How loudly the trigger presents itself.
   *
   * `control` (default) — the bordered `secondary` Button. Right where the trigger sits in
   * a card's action band among other bordered controls, as on the billing surfaces.
   *
   * `text` — borderless, `text.secondary` ink, no chassis. For a card whose chrome is
   * already low-emphasis text: on the analytics ROI hero this control sits beside a
   * `DisclosureTextButton`, and a bordered pill next to a text trigger reads as the more
   * important of the two when it is not — it scopes ONE card while the page's own date
   * picker, a few pixels above, scopes everything else. Same treatment as the billing
   * plan-comparison matrix's expand/collapse-all, which is where the pattern comes from.
   *
   * ⚠️ ADDITIVE, and the default is what keeps it so: the two billing consumers are
   * untouched. Do not flip the default to `text` — a trigger in a bordered action row that
   * loses its border stops looking like a control at all.
   */
  emphasis?: 'control' | 'text'
}) => {
  /**
   * The three parts, identical in both branches so the two emphases cannot drift in
   * spacing, weight or chevron size — which is the whole reason this component exists.
   *
   * The label takes `inherit` in the text branch so it recedes with the trigger on hover;
   * the VALUE is pinned to `text.primary` in both, because it is the thing being reported
   * and a trigger whose current choice is as quiet as its own label is one a reader has to
   * hunt for.
   */
  const content = (
    <>
      <Typography
        variant="body3"
        color={emphasis === 'text' ? 'inherit' : 'text.secondary'}
        component="span"
      >
        {label}
      </Typography>
      <Typography
        variant="body3"
        fontWeight={500}
        component="span"
        color={emphasis === 'text' ? 'text.primary' : undefined}
      >
        {value}
      </Typography>
      {/* TRAILING, unlike the disclosure's leading chevron. This is a SELECT: the chevron
          points at the menu it drops, so it belongs after the value it will replace. A
          disclosure's chevron is a state indicator for a region and leads. */}
      <ChevronDownIcon size={16} strokeWidth={1.5} />
    </>
  )

  if (emphasis === 'text') {
    return (
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={2}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        sx={theme => ({
          // The UA button chassis, removed — everything visual here is this branch's own.
          appearance: 'none',
          background: 'none',
          border: 'none',
          p: 0,
          font: 'inherit',
          width: 'fit-content',
          cursor: 'pointer',
          color: theme.palette.text.secondary,
          '&:hover': { color: theme.palette.text.primary },
          '&:focus-visible': {
            color: theme.palette.text.primary,
            outline: `2px solid ${theme.palette.brand[500]}`,
            outlineOffset: 2,
            borderRadius: 1,
          },
        })}
      >
        {content}
      </Stack>
    )
  }

  return (
    <Button variant="secondary" onClick={onClick} aria-label={ariaLabel}>
      <Stack direction="row" alignItems="center" spacing={2}>
        {content}
      </Stack>
    </Button>
  )
}
